import { convertCentsToEuros } from "../../services/transformers";
import { packages as packagesTemplate } from "./constants";
import type { Package, FirestoreTimestamp } from '../../services/payment.types';

// Inline type definitions based on usage/expected structure
interface PricingPackages { 
    [key: string]: { price: number; transactions?: number };
}
interface PricingInfo {
    packages?: PricingPackages;
    currencySymbol?: string;
    // other potential pricing fields...
}
interface UserPlanDetails {
    package: string;
    status: 'active' | 'inactive' | 'refunded' | string; // Allow other statuses if needed
    // other potential plan fields...
}
interface UserPlansData { 
    [key: string]: UserPlanDetails;
}


/**
 * Converts a Firestore timestamp or string date to a JavaScript Date object
 * @param timestamp Firestore timestamp or string date
 * @returns JavaScript Date object or null if input is invalid
 */
export const convertTimestampToDate = (timestamp: string | Date | FirestoreTimestamp | null | undefined): Date | null => {
    if (!timestamp) return null;
    
    // Handle Firestore timestamp format
    if (typeof timestamp === 'object' && '_seconds' in timestamp) {
        return new Date(timestamp._seconds * 1000);
    }
    
    // Handle Date object
    if (timestamp instanceof Date) {
        return timestamp;
    }
    
    // Handle string date
    if (typeof timestamp === 'string') {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
            return date;
        }
    }
    
    return null;
};

/**
 * Formats a date for display
 * @param date Date to format (string, Date object, or Firestore timestamp)
 * @param locale Locale to use for formatting (default: 'de-DE')
 * @returns Formatted date string
 */
export const formatValidUntil = (date: string | Date | FirestoreTimestamp | null | undefined, locale = 'de-DE') => {
    const validUntil = convertTimestampToDate(date);
    if (!validUntil) {
        // If date is invalid, return current date + 1 year
        const now = new Date();
        now.setFullYear(now.getFullYear() + 1);
        
        // Use German format for German locale
        if (locale === 'de-DE') {
            return now.toLocaleDateString('de-DE', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
        }
        
        return now.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }
    
    // Use German format for German locale
    if (locale === 'de-DE') {
        return validUntil.toLocaleDateString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    }
    
    return validUntil.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

export const calculateNewValidUntil = (currentValidUntil: string | FirestoreTimestamp | null | undefined, locale = 'de-DE') => {
    try {
        // Convert timestamp to Date object using our utility function
        const validUntilDate = convertTimestampToDate(currentValidUntil);
        
        if (!validUntilDate) {
            // If current valid until is invalid, use current date
            const now = new Date();
            now.setFullYear(now.getFullYear() + 1);
            return formatValidUntil(now, locale);
        }
        
        // Add 1 year to the valid date
        const newDate = new Date(validUntilDate);
        newDate.setFullYear(newDate.getFullYear() + 1);
        return formatValidUntil(newDate, locale);
    } catch (error) {
        console.error('Error calculating new valid until date:', error);
        // Fallback to current date + 1 year
        const now = new Date();
        now.setFullYear(now.getFullYear() + 1);
        return formatValidUntil(now, locale);
    }
};

/**
 * Calculates the effective display price for a package for a specific year, 
 * considering potential upgrades based on the user's existing plan for that year.
 * Returns the price difference for upgrades, otherwise the full price.
 * 
 * @param targetPackage The package being considered (template with default price).
 * @param year The specific tax year being considered.
 * @param userPlans The user's full plan status object.
 * @param pricing The current pricing structure from the API.
 * @returns The effective price in Euros for display purposes.
 */
export const calculateEffectiveDisplayPriceEuros = (
    targetPackage: Package,
    year: string,
    userPlans: UserPlansData | null | undefined,
    pricing: PricingInfo | null | undefined
): number => {
    
    const getPackagePriceInCents = (pkg: Package): number => {
        const apiPrice = pricing?.packages?.[pkg.id as keyof PricingPackages]?.price;
        return apiPrice ?? Math.round(pkg.price * 100); 
    }

    const fullTargetPriceInCents = getPackagePriceInCents(targetPackage);
    const planKey = `taxReports.${year}`;
    const existingPlan = userPlans?.[planKey];

    if (!existingPlan || existingPlan.status !== 'active') {
        return convertCentsToEuros(fullTargetPriceInCents);
    }

    const existingPackageId = existingPlan.package;
    const existingPackageIndex = packagesTemplate.findIndex(p => p.id === existingPackageId);
    const currentPackageIndex = packagesTemplate.findIndex(p => p.id === targetPackage.id);

    if (existingPackageIndex !== -1 && currentPackageIndex > existingPackageIndex) {
        const existingPackageTemplate = packagesTemplate.find(p => p.id === existingPackageId);
        const purchasedPriceInCents = existingPackageTemplate ? getPackagePriceInCents(existingPackageTemplate) : 0;

        if (purchasedPriceInCents > 0) {
            const differenceInCents = Math.max(0, fullTargetPriceInCents - purchasedPriceInCents);
            return convertCentsToEuros(differenceInCents);
        } else {
            return convertCentsToEuros(fullTargetPriceInCents);
        }
    } else {
        return convertCentsToEuros(fullTargetPriceInCents); 
    }
};
